/**
 * Contract Code field — CONTRACT-CODE-MANUAL-001 (Sep 2026)
 * Optional free-text code (uppercase, max 30) with suggestions from the
 * active contract-code mappings of the selected client. Shows the resulting
 * client-facing label "{client_product} - {contract_code}" (bulk-upload parity)
 * plus non-blocking hints when a typed code matches a mapping.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
export const CONTRACT_CODE_MAX_LENGTH = 30;

export const ContractCodeField = ({ value, clientCode, clientProduct, onChange }) => {
  const [mappings, setMappings] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bulk-upload/contract-mappings`)
      .then(res => setMappings(res.data?.mappings || []))
      .catch(() => setMappings([]));
  }, []);

  const code = (value || '').toUpperCase();
  const suggestions = useMemo(() => mappings
    .filter(m => m.client_code === clientCode && m.contract_code.includes(code) && m.contract_code !== code)
    .slice(0, 8), [mappings, clientCode, code]);
  const matched = useMemo(() => mappings.find(m => m.contract_code === code) || null, [mappings, code]);

  const clientMismatch = matched && clientCode && matched.client_code !== clientCode;
  const productMismatch = matched && clientProduct && matched.default_product &&
    matched.default_product.toLowerCase() !== clientProduct.toLowerCase();
  const externalPortal = matched?.delivery_method === 'external_portal';

  return (
    <>
    <div className="space-y-1.5" data-testid="contract-code-field">
      <Label className="text-slate-700 font-semibold text-sm flex items-center gap-1">
        Contract Code <span className="text-slate-400 font-normal text-xs">(optional)</span>
      </Label>
      <div className="relative">
        <Input
          value={code}
          onChange={(e) => onChange(e.target.value.toUpperCase().slice(0, CONTRACT_CODE_MAX_LENGTH))}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={clientCode ? 'e.g. AVD — type or pick a mapped code' : 'Select a client code first'}
          maxLength={CONTRACT_CODE_MAX_LENGTH}
          className="h-10 rounded-lg border-slate-200 bg-white text-sm font-mono shadow-[0_1px_2px_rgba(15,23,42,0.03)] placeholder:font-sans placeholder:text-slate-400"
          autoComplete="off"
          data-testid="input-contract-code"
        />
        {open && suggestions.length > 0 && (
          <ul
            className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg text-sm"
            data-testid="contract-code-suggestions"
          >
            {suggestions.map(m => (
              <li
                key={m.id || m.contract_code}
                onMouseDown={() => { onChange(m.contract_code); setOpen(false); }}
                className="flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-blue-50"
                data-testid={`contract-code-suggestion-${m.contract_code}`}
              >
                <span className="font-mono font-medium text-slate-800">{m.contract_code}</span>
                <span className="text-xs text-slate-500">{m.default_product}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>

      {/* Hints span the full row when the field sits in a grid.
          (CREATE-ORDER-UI-001: the redundant "Client Product: X" preview tag was removed — the
          selection is already highlighted under Select Product; `clientProduct` still drives the
          mismatch heads-up below.) */}
      {(clientMismatch || productMismatch || externalPortal) && (
      <div className="space-y-1.5 md:col-span-3">
      {(clientMismatch || productMismatch) && (
        <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="contract-code-mismatch-warning">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            <strong>{code}</strong> is mapped to{' '}
            {clientMismatch && <>client <strong>{matched.client_code}</strong></>}
            {clientMismatch && productMismatch && ' / '}
            {productMismatch && <>product <strong>{matched.default_product}</strong></>}
            . The order keeps the values you selected — this is only a heads-up.
          </span>
        </p>
      )}

      {externalPortal && (
        <p className="flex items-start gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1.5" data-testid="contract-code-external-portal-hint">
          <ExternalLink size={13} className="mt-0.5 shrink-0" />
          <span>Mapping <strong>{code}</strong> routes orders to the <strong>External Portal</strong> workflow — this order will follow that delivery path.</span>
        </p>
      )}
      </div>
      )}
    </>
  );
};
